import { useState, useEffect } from "react";
import { TextField } from "@mui/material";
import { Modal, ModalDialog, ModalOverflow, Typography, Stack, Button } from "@financeable-com-au/financeable-ui";

interface ProductQuoteNoteModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  handleSaveProductQuote: (productQuoteNotes: string) => void;
  isSaving: boolean;
  /** When "update", title and button show Update Quote Note / Update; otherwise Save Quote Note / Save */
  mode?: "save" | "update";
  /** Prefill for update mode */
  initialNotes?: string;
}

const modalWidth = "50%";

const ProductQuoteNoteModal = ({
  open,
  setOpen,
  handleSaveProductQuote,
  isSaving,
  mode = "save",
  initialNotes = "",
}: ProductQuoteNoteModalProps) => {
  const [productQuoteNotes, setProductQuoteNotes] = useState("");

  useEffect(() => {
    if (open) {
      setProductQuoteNotes(mode === "update" ? initialNotes : "");
    }
  }, [open, mode, initialNotes]);

  const handle = {
    submitQuoteNote: () => {
      handleSaveProductQuote(productQuoteNotes || "");
    },
    onChange: (value: string) => {
      setProductQuoteNotes(value);
    },
    onClose: () => {
      setProductQuoteNotes("");
      setOpen(false);
    },
  };

  const isUpdate = mode === "update";
  const title = isUpdate ? "Update Quote Recommendation Note" : "Save Quote Recommendation Note";
  const submitButtonText = isUpdate ? "Update" : "Save";
  const submitLoadingText = isUpdate ? "Updating..." : "Saving...";
  const submitLabel = isSaving ? submitLoadingText : submitButtonText;

  return (
    <Modal aria-labelledby="modal-title" aria-describedby="modal-desc" open={!!open} onClose={handle.onClose}>
      <ModalOverflow>
        <ModalDialog minWidth={modalWidth} maxWidth={modalWidth}>
          <Stack rowGap={5} alignItems="center">
            <Stack rowGap={2}>
              <Typography component="h2" id="modal-title" level="title-md" textAlign="center" bold>
                {title}
              </Typography>
              <Typography level="body-sm" textAlign="center">
                Describe why this loan has been recommended and how it meets customer requirements and objectives. If
                this loan is your final recommended product explain how it has been assessed as not unsuitable and why it
                best meets the customers objectives and requirements.
              </Typography>
            </Stack>

            <Stack style={{ width: "100%" }}>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={10}
                maxRows={10}
                placeholder="Type your quote note here..."
                value={productQuoteNotes}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handle.onChange(event.target.value)}
                variant="outlined"
                inputProps={{ maxLength: 3000 }}
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    alignItems: "flex-start",
                  },
                }}
                sx={{
                  "& textarea": {
                    maxHeight: 500,
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.25)",
                      borderRadius: "4px",
                    },
                  },
                }}
              />

              <Typography level="body-sm" style={{ alignSelf: "flex-end" }}>
                {productQuoteNotes.length}/3000
              </Typography>
            </Stack>

            <Stack rowGap={2} direction="row" columnGap={2}>
              <Button variant="secondary" fullWidth onClick={handle.onClose}>
                Cancel
              </Button>
              <Button variant="primary" fullWidth onClick={handle.submitQuoteNote} disabled={isSaving}>
                {submitLabel}
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export { ProductQuoteNoteModal };
